import BigNum from "../math/BigNum";
import { getRandomMonsterDescriptions } from "../monster/MonsterDescriptions";
import LayerDescription from "./LayerDescription";
import { SpecialTileTypes, TileTypeArray } from "./TileTypes";

// Some layers need to be directly referenced in the code.
export const LayerDescriptions = {
    SKY_LAYER: new LayerDescription(SpecialTileTypes.SKY, -1),
    GRASS_LAYER: new LayerDescription(SpecialTileTypes.GRASS, -1),
    DIRT_LAYER: new LayerDescription(SpecialTileTypes.DIRT, 1)
}

/**
 * @returns {LayerDescription[]}
 */
function createLayerDescriptionArray() {
    const layers = [];

    ///////////////////////////////
    // Add the special layers
    ///////////////////////////////

    layers.push(LayerDescriptions.SKY_LAYER);
    layers.push(LayerDescriptions.GRASS_LAYER);
    layers.push(LayerDescriptions.DIRT_LAYER);
    
    ///////////////////////////////
    // Standard layers
    ///////////////////////////////

    let nominalIndex = 2; // Dirt has the nominal index of 1

    for (let i = 0; i < TileTypeArray.length; i++) {
        layers.push(new LayerDescription(TileTypeArray[i], nominalIndex++));
    }

    ///////////////////////////////
    // Initialize Special Layers
    ///////////////////////////////
    
    // Note: The sky layer is a special case and doesn't reference its min/max depths.
    LayerDescriptions.SKY_LAYER.minDepth = -1000000;
    LayerDescriptions.SKY_LAYER.maxDepth = -1;
    LayerDescriptions.SKY_LAYER.baseHealth = new BigNum(20);

    // The grass layer is 1 tile thick and on the surface at 0 depth.
    LayerDescriptions.GRASS_LAYER.minDepth = -1;
    LayerDescriptions.GRASS_LAYER.maxDepth = 0;
    LayerDescriptions.GRASS_LAYER.baseHealth = new BigNum(20);
    LayerDescriptions.GRASS_LAYER.baseOre = new BigNum(1);

    LayerDescriptions.SKY_LAYER.initializeLayerValues(1, 1, 0.05, new BigNum(1), new BigNum(0));


    ///////////////////////////////
    // Initialize The Layers
    ///////////////////////////////
    
    let baseGold = 1; // starting gold
    let additiveBonusFactor = 10;
    let multiplicativeBonusFactor = 0.1; // mined multi grows the same as the pick multi.
    const oreStep = new BigNum(0.8); 
    const healthStep = new BigNum(12); 

    const healthStepMultiplier = new BigNum(10);
    const oreStepMultiplier = new BigNum(5);
    const additiveMultiplier = 2; // <-- this isn't used anymore
    let multiplicativeIncrement = 0.1;
    const multiplicativeIncrementIncrement = 0.1;  // <-- increments the incrementor

    // o    p   a    h
    // 1,   15, 2,   4     <--- DIRT  ore/points/additive/healthstep
    // 10,  16, 15,  100   grey stone
    // 100, 17, 30,  1e3   blue stone
    // 1e3, 18, 150, 1e4   grey orange granite
    // 1e4, 19, 2e2, 1e5   arkose

    let prevLayer = LayerDescriptions.GRASS_LAYER;
    const layerThickness = 500; // 400

    // To start at DIRT, we need to skip SKY and GRASS
    const dirtIndex = 2;
    for (let i = dirtIndex; i < layers.length; i++) {
        const layer = layers[i];

        layer.monsterDescriptions = getRandomMonsterDescriptions();

        layer.initializeLayerValues(baseGold, additiveBonusFactor, multiplicativeBonusFactor, oreStep, healthStep);

        //console.log('LAYER=' + layer.name + ' multiplicativeBonusFactor=' + multiplicativeBonusFactor + ' multiplicativeIncrement=' + multiplicativeIncrement);

        if (i < layers.length - 1) {
            layer.initializeLayer(prevLayer, layerThickness, i == dirtIndex);
        }
        else {
            // The last layer needs a thickness of -1 so that it is unbounded.
            layer.initializeLayer(prevLayer, -1, false);
        }

        prevLayer = layer;
        oreStep.mul(oreStepMultiplier);
        healthStep.mul(healthStepMultiplier);
        additiveBonusFactor *= additiveMultiplier; // <-- this isn't used anymore
        multiplicativeBonusFactor += multiplicativeIncrement;
        multiplicativeIncrement += multiplicativeIncrementIncrement;  // <-- make the incrementor bigger for the next layer

    }

    return layers;
}

export const LayerDescriptionArray = createLayerDescriptionArray();


function buildLayerDescriptionMap() {
    const map = {};

    for (let i = 0; i < LayerDescriptionArray.length; i++) {
        const layerDescription = LayerDescriptionArray[i];
        map[layerDescription.id] = layerDescription;
    }

    return map;
}

/**
 * @constant
 * @type {Object}
 */
export const LayerDescriptionMap = buildLayerDescriptionMap();

/**
 * This is an O(N) function that is only used by some test code in ValueCalculator.
 * @param {number} tileRow 
 * @returns {LayerDescription}
 */
export function getLayerDescriptionForRow(tileRow) {

    for (let i = 0; i < LayerDescriptionArray.length; i++) {
        const layerDescription = LayerDescriptionArray[i];
        if (layerDescription.minDepth <= tileRow && tileRow < layerDescription.maxDepth) {
            return layerDescription;
        }
    }

    return LayerDescriptionArray[LayerDescriptionArray.length - 1];
}

/**
 * @param {LayerDescription} currentLayerDescription
 * @param {BigNum} health 
 * @returns {LayerDescription}
 */
export function findLayerForHealth(currentLayerDescription, health) {
    let layer = currentLayerDescription;

    // Find the layer if the health is greater than the current layer's min health.
    while (layer && layer.prevLayer && layer.baseHealth.greaterThan(health)) {
        layer = layer.prevLayer;
    }

    while (layer && layer.nextLayer && layer.maxHealth.lessThan(health)) {
        layer = layer.nextLayer;
    }

    if (!layer) {
        console.log('LayerDescriptions.findLayerForHealth() null layer. using default');
        return currentLayerDescription;
    }

    return layer;
}